'use client'

import { useState } from 'react'
import { supabase } from '@/lib/supabase'
import { useMenuPackages } from '../hooks/useMenu'
import { useCompanySettings } from '@/features/contracts/hooks/useCompanySettings'
import { Button } from '@planner/ui'
import { Card, CardContent, CardHeader, CardTitle } from '@planner/ui'
import { Input } from '@planner/ui'
import { Label } from '@planner/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@planner/ui'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@planner/ui'
import type { MenuPackage } from '@planner/types'
import { Plus, Edit, Trash2 } from 'lucide-react'
import { useRouter } from 'next/navigation'
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog'

export function MenuPackagesList() {
  const { packages, loading, deletePackage, fetchPackages } = useMenuPackages()
  const { settings, saveSettings } = useCompanySettings()
  const router = useRouter()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [packageToDelete, setPackageToDelete] = useState<string | null>(null)
  const [feeDialogOpen, setFeeDialogOpen] = useState(false)
  const [feeFormData, setFeeFormData] = useState({
    custom_menu_fee_enabled: false,
    custom_menu_fee_amount: 0,
    custom_menu_fee_type: 'per_person' as 'per_person' | 'fixed',
  })
  const [feeSaving, setFeeSaving] = useState(false)

  const handleDeleteClick = (id: string) => {
    setPackageToDelete(id)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (packageToDelete) {
      try {
        await deletePackage(packageToDelete)
        setPackageToDelete(null)
      } catch (error) {
        console.error('Error deleting menu package:', error)
      }
    }
  }

  const handleFeeClick = () => {
    if (settings) {
      setFeeFormData({
        custom_menu_fee_enabled: settings.custom_menu_fee_enabled || false,
        custom_menu_fee_amount: settings.custom_menu_fee_amount || 0,
        custom_menu_fee_type: (settings.custom_menu_fee_type || 'per_person') as 'per_person' | 'fixed',
      })
    }
    setFeeDialogOpen(true)
  }

  const handleFeeSave = async () => {
    setFeeSaving(true)
    try {
      await saveSettings({
        ...settings,
        custom_menu_fee_enabled: feeFormData.custom_menu_fee_enabled,
        custom_menu_fee_amount: feeFormData.custom_menu_fee_amount,
        custom_menu_fee_type: feeFormData.custom_menu_fee_type,
      } as any)
      setFeeDialogOpen(false)
    } catch (error) {
      console.error('Error saving fee:', error)
      alert('Błąd podczas zapisywania opłaty')
    } finally {
      setFeeSaving(false)
    }
  }

  if (loading) {
    return <div className="p-8">Ładowanie...</div>
  }

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-4 md:mb-6">
        <h1 className="text-2xl md:text-3xl font-bold">Pakiety menu</h1>
        <Button onClick={() => router.push('/menu/packages/new')}>
          <Plus className="w-4 h-4 mr-2" />
          Dodaj pakiet
        </Button>
      </div>

      {/* Global custom menu fee configuration */}
      <Card className="mb-4 md:mb-6">
        <CardHeader className="p-4 md:p-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="flex-1">
              <CardTitle>Opłata za menu niestandardowe</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Ustaw globalną opłatę, która będzie doliczana do menu niestandardowego (gdy klient wybierze menu niestandardowe zamiast pakietu).
              </p>
            </div>
            <Button variant="outline" onClick={handleFeeClick} className="w-full md:w-auto">
              Zmień
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-4 md:p-6">
          {settings?.custom_menu_fee_enabled && settings.custom_menu_fee_amount ? (
            <div className="text-sm">
              <span className="text-muted-foreground">Aktualna opłata: </span>
              <span className="font-medium">
                {settings.custom_menu_fee_amount.toFixed(2)} zł
                {settings.custom_menu_fee_type === 'per_person' ? ' / os' : ' (jednorazowo)'}
              </span>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">
              Opłata za menu niestandardowe nie jest włączona
            </div>
          )}
        </CardContent>
      </Card>

      {packages.length === 0 ? (
        <Card>
          <CardContent className="p-4 md:p-8 text-center text-muted-foreground">
            Brak pakietów. Dodaj pierwszy pakiet menu.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3 md:gap-4">
          {packages.map((pkg: MenuPackage) => (
            <Card key={pkg.id}>
              <CardHeader>
                <CardTitle>{pkg.name}</CardTitle>
              </CardHeader>
              <CardContent>
                {pkg.description && (
                  <p className="text-sm text-muted-foreground mb-4">{pkg.description}</p>
                )}
                <div className="text-2xl font-bold mb-4">
                  {pkg.price_per_person.toFixed(2)} zł/os
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => router.push(`/menu/packages/${pkg.id}/edit`)}
                    title="Edytuj pakiet"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDeleteClick(pkg.id)}
                    title="Usuń pakiet"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDeleteConfirm}
        title="Czy na pewno chcesz usunąć ten pakiet menu?"
        description="Tej operacji nie można cofnąć."
      />

      <Dialog open={feeDialogOpen} onOpenChange={setFeeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Opłata za menu niestandardowe</DialogTitle>
            <DialogDescription>
              Ustaw globalną opłatę, która będzie doliczana do menu niestandardowego (gdy klient wybierze menu niestandardowe zamiast dowolnego pakietu).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="custom_menu_fee_enabled"
                checked={feeFormData.custom_menu_fee_enabled}
                onChange={(e) => setFeeFormData({ ...feeFormData, custom_menu_fee_enabled: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300"
              />
              <Label htmlFor="custom_menu_fee_enabled" className="font-normal cursor-pointer">
                Włącz opłatę za menu niestandardowe
              </Label>
            </div>

            {feeFormData.custom_menu_fee_enabled && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6">
                <div className="space-y-2">
                  <Label htmlFor="custom_menu_fee_amount">Kwota opłaty (zł)</Label>
                  <Input
                    id="custom_menu_fee_amount"
                    type="number"
                    min="0"
                    step="0.01"
                    value={feeFormData.custom_menu_fee_amount}
                    onChange={(e) => setFeeFormData({ ...feeFormData, custom_menu_fee_amount: parseFloat(e.target.value) || 0 })}
                    placeholder="0.00"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="custom_menu_fee_type">Typ opłaty</Label>
                  <Select
                    value={feeFormData.custom_menu_fee_type}
                    onValueChange={(value) => setFeeFormData({ ...feeFormData, custom_menu_fee_type: value as 'per_person' | 'fixed' })}
                  >
                    <SelectTrigger id="custom_menu_fee_type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="per_person">Za osobę</SelectItem>
                      <SelectItem value="fixed">Jednorazowo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFeeDialogOpen(false)}>
              Anuluj
            </Button>
            <Button onClick={handleFeeSave} disabled={feeSaving}>
              {feeSaving ? 'Zapisywanie...' : 'Zapisz'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

